import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ContactKind, ContactStatus, CreateContactInput } from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useContact, useCreateContact, useUpdateContact } from '../../api/contacts';

export type ContactFormShape = {
  displayName: string;
  kind: ContactKind;
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phone: string;
  alternatePhone: string;
  source: string;
  status: ContactStatus;
};

export const EMPTY_CONTACT_FORM: ContactFormShape = {
  displayName: '',
  kind: 'PERSON',
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phone: '',
  alternatePhone: '',
  source: '',
  status: 'ACTIVE',
};

const trimOrUndef = (v: string) => {
  const t = v.trim();
  return t === '' ? undefined : t;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Mirrors the backend's `createContactBody` limits (contacts.schemas.ts). */
export function validateContactForm(form: ContactFormShape): Record<string, string> {
  const errors: Record<string, string> = {};
  const displayName = form.displayName.trim();
  if (displayName.length < 1) {
    errors.displayName = 'Enter a display name.';
  } else if (displayName.length > 200) {
    errors.displayName = 'Display name must be 200 characters or fewer.';
  }
  if (form.firstName.trim().length > 120) {
    errors.firstName = 'First name must be 120 characters or fewer.';
  }
  if (form.lastName.trim().length > 120) {
    errors.lastName = 'Last name must be 120 characters or fewer.';
  }
  if (form.companyName.trim().length > 200) {
    errors.companyName = 'Company name must be 200 characters or fewer.';
  }
  const email = form.email.trim();
  if (email.length > 0) {
    if (email.length > 254 || !EMAIL_RE.test(email)) {
      errors.email = 'Enter a valid email address.';
    }
  }
  if (form.phone.trim().length > 40) {
    errors.phone = 'Phone must be 40 characters or fewer.';
  }
  if (form.alternatePhone.trim().length > 40) {
    errors.alternatePhone = 'Alternate phone must be 40 characters or fewer.';
  }
  if (form.source.trim().length > 120) {
    errors.source = 'Source must be 120 characters or fewer.';
  }
  return errors;
}

export function ContactFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const existing = useContact(id);
  const create = useCreateContact();
  const update = useUpdateContact(id ?? '');

  const [form, setForm] = useState<ContactFormShape>(EMPTY_CONTACT_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && existing.data) {
      const c = existing.data;
      setForm({
        displayName: c.displayName,
        kind: c.kind,
        firstName: c.firstName ?? '',
        lastName: c.lastName ?? '',
        companyName: c.companyName ?? '',
        email: c.email ?? '',
        phone: c.phone ?? '',
        alternatePhone: c.alternatePhone ?? '',
        source: c.source ?? '',
        status: c.status,
      });
    }
  }, [isEdit, existing.data]);

  const set = (key: keyof ContactFormShape) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  };

  const busy = create.isPending || update.isPending;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validateContactForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    const payload: CreateContactInput = {
      displayName: form.displayName.trim(),
      kind: form.kind,
      firstName: trimOrUndef(form.firstName),
      lastName: trimOrUndef(form.lastName),
      companyName: trimOrUndef(form.companyName),
      email: trimOrUndef(form.email),
      phone: trimOrUndef(form.phone),
      alternatePhone: trimOrUndef(form.alternatePhone),
      source: trimOrUndef(form.source),
      status: form.status,
    };
    try {
      const saved =
        isEdit && id ? await update.mutateAsync(payload) : await create.mutateAsync(payload);
      toast.success(isEdit ? 'Contact updated.' : 'Contact created.');
      navigate(`/contacts/${saved.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message =
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not save the contact.';
        setError(message);
        toast.error(message);
      }
    }
  }

  if (isEdit && existing.isPending) return <PageState kind="loading" message="Loading contact…" />;
  if (isEdit && existing.isError) {
    return <PageState kind="error" message={existing.error.message} />;
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 560 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>
        {isEdit ? 'Edit contact' : 'New contact'}
      </h1>
      <Card>
        <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Display name"
            required
            value={form.displayName}
            onChange={set('displayName')}
            error={fieldErrors.displayName}
          />
          <Select
            label="Kind"
            value={form.kind}
            options={[
              { value: 'PERSON', label: 'Person' },
              { value: 'COMPANY', label: 'Company' },
            ]}
            onChange={set('kind')}
          />
          <TextField
            label="First name"
            value={form.firstName}
            onChange={set('firstName')}
            error={fieldErrors.firstName}
          />
          <TextField
            label="Last name"
            value={form.lastName}
            onChange={set('lastName')}
            error={fieldErrors.lastName}
          />
          <TextField
            label="Company"
            value={form.companyName}
            onChange={set('companyName')}
            error={fieldErrors.companyName}
          />
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={set('email')}
            error={fieldErrors.email}
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={set('phone')}
            error={fieldErrors.phone}
          />
          <TextField
            label="Alternate phone"
            value={form.alternatePhone}
            onChange={set('alternatePhone')}
            error={fieldErrors.alternatePhone}
          />
          <TextField
            label="Source"
            value={form.source}
            onChange={set('source')}
            error={fieldErrors.source}
          />
          <Select
            label="Status"
            value={form.status}
            options={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'ARCHIVED', label: 'Archived' },
              { value: 'BLOCKED', label: 'Blocked' },
            ]}
            onChange={set('status')}
          />
          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create contact'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(isEdit && id ? `/contacts/${id}` : '/contacts')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/contacts">← All contacts</Link>
      </p>
    </div>
  );
}
